import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  Slider,
  CircularProgress,
  Tooltip,
  TextField
} from '@mui/material';
import { PlayArrow, Pause, Save, Cancel, SkipNext } from '@mui/icons-material';
import WaveSurfer from 'wavesurfer.js';
// Temporarily comment out Regions plugin
// import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.js';
import RegionsPlugin, { type Region } from 'wavesurfer.js/dist/plugins/regions.js'; // Re-enable Regions plugin
// import { WaveSurferEvents } from 'wavesurfer.js'; // Add WaveSurferEvents import
// Add Minimap plugin import
import Minimap from 'wavesurfer.js/dist/plugins/minimap.js';

interface WaveformEditorDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (key: string, inPoint: number, outPoint: number) => void; // Removed gain parameter
  audioUrl: string | null;
  initialInPoint: number;
  initialOutPoint: number;
  initialGain?: number; // Kept for display purposes only (not saved)
  lineKey: string;
  songTitle?: string;
  songArtist?: string;
  peaks?: number[];
  onPeaksGenerated?: (peaks: number[]) => void;
}

export const WaveformEditorDialog = React.memo<WaveformEditorDialogProps>((props) => {
  const {
    open,
    onClose,
    onSave,
    audioUrl,
    initialInPoint,
    initialOutPoint,
    initialGain = 0, // Default to 0dB if not provided
    lineKey,
    songTitle,
    songArtist,
    peaks,
    onPeaksGenerated,
  } = props;

  // *** 1. Log Props ***
  console.log('[WaveformEditor DEBUG] Props received:', { open, audioUrl, initialInPoint, initialOutPoint, initialGain, lineKey, peaks });
  // Log received peaks prop length specifically
  console.log(`[WaveformEditor DEBUG] Received peaks prop length: ${peaks?.length ?? 'undefined'}`);

  const [waveformContainer, setWaveformContainer] = useState<HTMLDivElement | null>(null);
  const wavesurfer = useRef<WaveSurfer | null>(null);
  // const wsRegions = useRef<RegionsPlugin | null>(null); // Commented out
  // const activeRegionRef = useRef<Region | null>(null); // Commented out
  const wsRegions = useRef<RegionsPlugin | null>(null); // Re-enable
  const activeRegionRef = useRef<Region | null>(null); // Re-enable
  // Audio gain refs for applying gain > 0dB (WaveSurfer's setVolume only supports 0-1)
  const audioContextRef = useRef<AudioContext | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const mediaSourceRef = useRef<MediaElementAudioSourceNode | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const animationFrameRef = useRef<number>();
  const lastUpdateTimeRef = useRef<number>(0);
  // Re-enable region state
  const [currentInPoint, setCurrentInPoint] = useState(initialInPoint);
  const [currentOutPoint, setCurrentOutPoint] = useState(initialOutPoint);

  // Live refs mirroring the in/out points. The WaveSurfer event handlers are
  // subscribed once (the subscription effect deliberately omits function deps to
  // avoid re-creating the instance), so reading currentOutPoint directly inside
  // them captures a stale value. Reading these refs gives the latest edited
  // points — e.g. so the audioprocess stop-check stops at the NEW out point.
  const inPointRef = useRef(initialInPoint);
  const outPointRef = useRef(initialOutPoint);
  useEffect(() => { inPointRef.current = currentInPoint; }, [currentInPoint]);
  useEffect(() => { outPointRef.current = currentOutPoint; }, [currentOutPoint]);

  // Reset current values when dialog opens with new initial values
  useEffect(() => {
    if (open) {
      setCurrentInPoint(initialInPoint);
      setCurrentOutPoint(initialOutPoint);
    }
  }, [open, initialInPoint, initialOutPoint]);
  const [isPlayingRegion, setIsPlayingRegion] = useState(false); 
  // Add state for zoom level
  const [minPxPerSec, setMinPxPerSec] = useState<number>(100); // Default zoom level
  // Add ref for play region button
  const playRegionButtonRef = useRef<HTMLButtonElement | null>(null);

  // Re-enable region update handler
  const handleRegionUpdateEnd = useCallback((region: Region) => {
    console.log('[WaveformEditor] Region update end:', region.start, region.end);
    console.log('[WaveformEditor] Updating currentInPoint to', region.start);
    console.log('[WaveformEditor] Updating currentOutPoint to', region.end);
    setCurrentInPoint(region.start);
    setCurrentOutPoint(region.end);
    activeRegionRef.current = region; // Keep track of the active region
  }, []); // Remove dependencies that cause constant re-rendering

  // Region bounds are updated only when user drags the region
  // Playback uses activeRegionRef.current.start/end which are always current

  // --- Define Event Handlers using useCallback ---
  const handleReady = useCallback(() => {
    // *** 5. Log Ready Event ***
    console.log('[WaveformEditor DEBUG] handleReady triggered');
    if (!wavesurfer.current || !wsRegions.current) {
        console.error('[WaveformEditor DEBUG] handleReady: WaveSurfer or Regions plugin not ready!');
        setIsLoading(false);
        setError('Failed to initialize waveform regions.');
        return;
    }
    console.log(`[WaveformEditor] WaveSurfer 'ready' event fired. wsRegions available: ${!!wsRegions.current}`);
    const dur = wavesurfer.current.getDuration();
    setDuration(dur);
    setIsLoading(false);

    // Apply gain/volume setting (convert dB to linear)
    const linearGain = Math.pow(10, initialGain / 20);
    console.log(`[WaveformEditor] Applying gain: ${initialGain}dB (linear: ${linearGain.toFixed(4)})`);

    if (linearGain <= 1) {
      // For gains <= 0dB, use WaveSurfer's built-in volume (0-1 range)
      wavesurfer.current.setVolume(linearGain);
      console.log(`[WaveformEditor] Applied gain via setVolume: ${linearGain.toFixed(4)}`);
    } else {
      // For gains > 0dB, we need to use Web Audio API GainNode
      // WaveSurfer's setVolume() only accepts 0-1 range
      try {
        const mediaElement = wavesurfer.current.getMediaElement();
        if (mediaElement && !mediaSourceRef.current) {
          // Create AudioContext if needed
          if (!audioContextRef.current) {
            audioContextRef.current = new AudioContext();
          }

          // Create source from media element (can only be done once per element)
          mediaSourceRef.current = audioContextRef.current.createMediaElementSource(mediaElement);

          // Create gain node
          gainNodeRef.current = audioContextRef.current.createGain();
          gainNodeRef.current.gain.value = linearGain;

          // Connect: source -> gain -> destination
          mediaSourceRef.current.connect(gainNodeRef.current);
          gainNodeRef.current.connect(audioContextRef.current.destination);

          console.log(`[WaveformEditor] Applied gain via Web Audio GainNode: ${linearGain.toFixed(4)}`);
        } else if (gainNodeRef.current) {
          // Update existing gain node
          gainNodeRef.current.gain.value = linearGain;
          console.log(`[WaveformEditor] Updated existing GainNode: ${linearGain.toFixed(4)}`);
        }
      } catch (err) {
        console.error('[WaveformEditor] Failed to apply gain via Web Audio:', err);
        // Fallback: clamp to 1.0 (no boost, but at least no error)
        wavesurfer.current.setVolume(1);
      }
    }

    // --- Calculate and Apply Initial Zoom ---
    if (waveformContainer) {
        const containerWidth = waveformContainer.offsetWidth;
        const regionDuration = initialOutPoint - initialInPoint;
        if (containerWidth > 0 && regionDuration > 0.1) { 
            // Calculate zoom to show a good portion of the region
            // Using a smaller factor to show more context
            const visibleDuration = regionDuration * 2; // Show 2x the region duration
            let calculatedZoom = containerWidth / visibleDuration;
            calculatedZoom = Math.max(10, Math.min(calculatedZoom, 500));
            console.log(`[WaveformEditor DEBUG] handleReady Zoom Calculation: containerWidth=${containerWidth}, regionDuration=${regionDuration.toFixed(2)}, visibleDuration=${visibleDuration.toFixed(2)}, calculatedZoom=${calculatedZoom.toFixed(2)}`);
            setMinPxPerSec(calculatedZoom);
            wavesurfer.current.zoom(calculatedZoom);
            
            // Position playhead at IN point
            const inPoint = initialInPoint;
            const duration = wavesurfer.current.getDuration();
            if (duration > 0) {
                // Calculate the position in pixels of the IN point
                const inPointPosition = inPoint * calculatedZoom;
                // Position to put IN point 20% from left edge
                const desiredLeftMargin = containerWidth * 0.2;
                
                // Set the scroll position to place IN point at our desired left margin
                // We need to scroll just enough so the IN point appears 20% from left
                console.log(`[WaveformEditor DEBUG] Positioning view: inPointPosition=${inPointPosition.toFixed(0)}px, desiredLeftMargin=${desiredLeftMargin.toFixed(0)}px`);
                
                // Apply the zoom first
                wavesurfer.current.zoom(calculatedZoom);
                
                // Then seek to the IN point to set the playhead
                const inPointProgress = inPoint / duration;
                wavesurfer.current.seekTo(inPointProgress);
                setCurrentTime(inPoint);
                
                // Finally, adjust the scroll position using scrollLeft
                // Added timeout to ensure the seekTo and zoom have completed
                setTimeout(() => {
                    if (wavesurfer.current && wavesurfer.current.getWrapper()) {
                        try {
                            const scrollPosition = Math.max(0, inPointPosition - desiredLeftMargin);
                            console.log(`[WaveformEditor DEBUG] Setting scrollLeft to ${scrollPosition.toFixed(0)}px`);
                            
                            // Access the scroll container and set the scroll position directly
                            const scrollElement = wavesurfer.current.getWrapper().parentNode;
                            if (scrollElement && 'scrollLeft' in scrollElement) {
                                scrollElement.scrollLeft = scrollPosition;
                                console.log('[WaveformEditor DEBUG] Scroll position set successfully');
                            } else {
                                console.warn('[WaveformEditor DEBUG] Could not find scrollable parent element');
                            }
                        } catch (e) {
                            console.error('[WaveformEditor DEBUG] Error setting scroll position:', e);
                        }
                    }
                }, 100);
            } else {
                console.log(`[WaveformEditor DEBUG] Cannot calculate IN point progress, duration is 0.`);
            }
        } else {
             console.log('[WaveformEditor DEBUG] Skipping initial zoom calculation (container width or region duration too small).');
             wavesurfer.current.zoom(minPxPerSec); // Apply default zoom if calculation skipped
             
             // Simple IN point positioning for default zoom
             const duration = wavesurfer.current.getDuration();
             if (duration > 0) {
                const inPointProgress = initialInPoint / duration;
                console.log(`[WaveformEditor DEBUG] Seeking to IN point: ${inPointProgress.toFixed(3)} (time: ${initialInPoint.toFixed(2)}s)`);
                wavesurfer.current.seekTo(inPointProgress);
                setCurrentTime(initialInPoint);
             } else {
                 console.log(`[WaveformEditor DEBUG] Cannot calculate IN point progress, duration is 0.`);
             }
        }
    } else {
        console.warn('[WaveformEditor DEBUG] waveformContainer not available in handleReady for zoom calculation.');
        wavesurfer.current.zoom(minPxPerSec); // Apply default zoom if container not ready
        
        // Simple positioning if container isn't ready
        const duration = wavesurfer.current.getDuration();
        if (duration > 0) {
            const inPointProgress = initialInPoint / duration;
            console.log(`[WaveformEditor DEBUG] Seeking to IN point: ${inPointProgress.toFixed(3)} (time: ${initialInPoint.toFixed(2)}s)`);
            wavesurfer.current.seekTo(inPointProgress);
            setCurrentTime(initialInPoint);
        } else {
            console.log(`[WaveformEditor DEBUG] Cannot calculate IN point progress, duration is 0.`);
        }
    }
    // ----------------------------------------

    // Re-enable region creation
    
    // Clear any previous regions and add the initial one
    console.log('[WaveformEditor DEBUG] handleReady: Clearing previous regions.');
    wsRegions.current.clearRegions();
    
    const regionParams = {
      start: Math.max(0, initialInPoint), // Use initial values for setup
      end: Math.min(dur, initialOutPoint), // Use initial values for setup
      color: 'rgba(0, 123, 255, 0.3)', // Blue with 30% opacity for better visibility
      drag: true,
      resize: true,
    };
    console.log('[WaveformEditor DEBUG] handleReady: Attempting to add region with params:', regionParams);
    try {
      const region = wsRegions.current.addRegion(regionParams);
      console.log('[WaveformEditor DEBUG] handleReady: Region added successfully:', region);
      activeRegionRef.current = region; // Store ref to the created region
    } catch (regionError) {
      console.error('[WaveformEditor DEBUG] handleReady: Error adding region:', regionError);
      setError('Failed to create initial editing region.');
      activeRegionRef.current = null; // Ensure ref is null on error
    }

    // Attach listener for region updates
    // Try multiple event names to ensure we catch region changes
    if (wsRegions.current && activeRegionRef.current) { // Check if region was added before attaching listener
        console.log('[WaveformEditor DEBUG] handleReady: Attaching region event listeners.');
        
        // Use the correct event names from the WaveSurfer Regions plugin
        wsRegions.current.on('region-updated', handleRegionUpdateEnd);
        wsRegions.current.on('region-update', handleRegionUpdateEnd);
        
        // Also try direct region-specific events
        if (activeRegionRef.current) {
            // Monitor the specific region for changes
            activeRegionRef.current.on('update', () => {
                console.log('[WaveformEditor] Direct region update event fired!');
                if (activeRegionRef.current) {
                    handleRegionUpdateEnd(activeRegionRef.current);
                }
            });
            activeRegionRef.current.on('update-end', () => {
                console.log('[WaveformEditor] Direct region update-end event fired!');
                if (activeRegionRef.current) {
                    handleRegionUpdateEnd(activeRegionRef.current);
                }
            });
        }
        
        console.log('[WaveformEditor DEBUG] Region event listeners attached successfully.');
        
        // Note: Polling removed due to circular dependency issues
        console.log('[WaveformEditor DEBUG] Region setup complete.');
    } else {
        console.warn('[WaveformEditor DEBUG] handleReady: Skipping region event listener attachment due to missing region.');
    }
    
  }, [initialInPoint, initialOutPoint, initialGain, handleRegionUpdateEnd, peaks, onPeaksGenerated, waveformContainer]);

  const handleError = useCallback((err: string | Error) => {
    const errorMsg = typeof err === 'string' ? err : err.message;
    console.error(`[WaveformEditor] WaveSurfer 'error' event fired:`, errorMsg);
    setError(`Failed to load audio: ${errorMsg}`);
    setIsLoading(false);
  }, []);
  
  const handleAudioprocess = useCallback((time: number) => {
    setCurrentTime(time);

    // FORCE check if we should stop at OUT point. Read the live ref, not the
    // captured currentOutPoint — this handler is subscribed to WaveSurfer once,
    // so a captured value would be stale after the user drags the out point.
    const outPoint = outPointRef.current;
    if (wavesurfer.current && time >= outPoint - 0.1) {
      console.log(`STOPPING AT OUT POINT: ${time.toFixed(3)} >= ${outPoint.toFixed(3)}`);
      wavesurfer.current.pause();
      setIsPlaying(false);
      setIsPlayingRegion(false);
    }
  }, []);

  const handleSeeking = useCallback((time: number) => setCurrentTime(time), []);
  const handlePlay = useCallback(() => {
    setIsPlaying(true);
    // Start high-frequency time updates at 30fps
    const updateTime = () => {
      if (wavesurfer.current && wavesurfer.current.isPlaying()) {
        const now = performance.now();
        // Update at ~33ms intervals (30fps)
        if (now - lastUpdateTimeRef.current >= 33) {
          setCurrentTime(wavesurfer.current.getCurrentTime());
          lastUpdateTimeRef.current = now;
        }
        animationFrameRef.current = requestAnimationFrame(updateTime);
      }
    };
    updateTime();
  }, []);
  const handlePause = useCallback(() => {
    console.log("[WaveformEditor DEBUG] handlePause triggered. Setting isPlaying=false, isPlayingRegion=false.");
    setIsPlaying(false);
    setIsPlayingRegion(false); // Reset flag on any pause
    // Stop the animation frame updates
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  // No dependencies needed here as it only calls setters
  }, []);
  const handleFinish = useCallback(() => {
    console.log('[WaveformEditor] handleFinish triggered');
    setIsPlaying(false);
    setIsPlayingRegion(false); // Reset region flag when playback finishes
  }, []);


  // Main Initialization Effect
  useEffect(() => {
    console.log('[WaveformEditor] useEffect triggered. Open:', open, 'Audio URL:', audioUrl, 'Peaks provided:', !!peaks);
    if (!open || !waveformContainer) return;

    // Create abort controller for this effect
    const abortController = new AbortController();

    setIsLoading(true);
    setError(null);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);

    if (wavesurfer.current) {
      console.log('[WaveformEditor] Destroying previous WaveSurfer instance.');
      try {
        wavesurfer.current.destroy();
      } catch (e) {
        console.warn('[WaveformEditor] Error destroying previous instance:', e);
      }
      wavesurfer.current = null;
      wsRegions.current = null; // Also clear regions ref
      activeRegionRef.current = null;
    }

    // Check if aborted before initializing
    if (abortController.signal.aborted) {
      console.log('[WaveformEditor] Effect aborted before initialization');
      return;
    }

    console.log(`[WaveformEditor] Initializing WaveSurfer...`);
    let wsInstance: WaveSurfer | null = null;
    try {
        const options = {
            container: waveformContainer,
            waveColor: 'rgb(200, 0, 200)',
            progressColor: 'rgb(100, 0, 100)',
            height: 100,
            normalize: !peaks,
            peaks: peaks ? [peaks] : undefined,
        };
        console.log('[WaveformEditor DEBUG] WaveSurfer create options:', options);
        wsInstance = WaveSurfer.create(options);
        wavesurfer.current = wsInstance;
        // wsRegions.current = wsInstance.plugins[0] as RegionsPlugin; // Store reference to Regions plugin
        // Re-enable plugin registration
        wsRegions.current = wsInstance.registerPlugin(RegionsPlugin.create());
        // Register Minimap plugin with default options
        console.log('[WaveformEditor DEBUG] Registering Minimap plugin.');
        wsInstance.registerPlugin(Minimap.create({
            // Optional: customize Minimap appearance if needed
            // height: 30,
            // waveColor: '#ddd',
            // progressColor: '#999',
        }));

        // Load audio separately if URL exists
        if (audioUrl) {
          console.log(`[WaveformEditor] Loading audio URL: ${audioUrl}`);

          // CRITICAL: Never allow S3 URLs to be loaded directly - prevent CORS errors
          console.log(`[AUDIO DEBUG - WaveformEditor] ===== S3 URL BLOCKING CHECK =====`);
          console.log(`[AUDIO DEBUG - WaveformEditor] audioUrl: "${audioUrl}"`);
          console.log(`[AUDIO DEBUG - WaveformEditor] S3 domain check: ${audioUrl.includes('s3.amazonaws.com')}`);
          console.log(`[AUDIO DEBUG - WaveformEditor] S3 region check: ${audioUrl.includes('s3.us-west-2.amazonaws.com')}`);

          if (audioUrl.includes('s3.amazonaws.com') || audioUrl.includes('s3.us-west-2.amazonaws.com')) {
            console.error('[AUDIO DEBUG - WaveformEditor] ❌ BLOCKED S3 URL:', audioUrl);
            console.error('[AUDIO DEBUG - WaveformEditor] ❌ This should not happen if caching is working correctly');
            setIsLoading(false);
            setError('S3 audio files must be cached locally first. Please use the caching system instead of loading directly from S3.');
            return;
          } else {
            console.log(`[AUDIO DEBUG - WaveformEditor] ✅ Non-S3 URL approved for loading`);
          }

          // Wrap load in a promise to handle abort errors
          const loadPromise = wsInstance.load(audioUrl);
          
          // Handle the load promise
          loadPromise.catch((error) => {
            // Check if it's an abort error
            if (error && error.name === 'AbortError') {
              console.log('[WaveformEditor] Audio load was aborted (expected during cleanup)');
            } else if (!abortController.signal.aborted) {
              // Only log real errors if we haven't been aborted
              console.error('[WaveformEditor] Error loading audio:', error);
              setError('Failed to load audio');
              setIsLoading(false);
            }
          });
        } else {
          console.error('[WaveformEditor] No audioUrl provided, cannot load audio.');
          setError('No audio URL available');
          setIsLoading(false);
          // Potentially return early or handle this state
        }

        // Only attach listeners if not aborted
        if (!abortController.signal.aborted) {
          // Attach listeners
          wsInstance.once('ready', handleReady);
          wsInstance.once('error', handleError);
          wsInstance.on('audioprocess', handleAudioprocess);
          wsInstance.on('seeking', handleSeeking);
          wsInstance.on('play', handlePlay);
          wsInstance.on('pause', handlePause);
          wsInstance.on('finish', handleFinish);
        }

    } catch (initError) {
        console.error('[WaveformEditor] Error initializing WaveSurfer:', initError);
        setError(`Initialization failed: ${initError instanceof Error ? initError.message : String(initError)}`);
        setIsLoading(false);
    }

    // Cleanup function
    return () => {
        // Signal abort to prevent errors
        abortController.abort();

        if (wsInstance) {
             console.log("[WaveformEditor] Cleaning up listeners and destroying instance");
             // Use the local variable wsInstance for cleanup
             // Re-enable region plugin cleanup
             if (wsRegions.current) {
                 wsRegions.current.un('region-updated', handleRegionUpdateEnd);
                 wsRegions.current.un('region-update', handleRegionUpdateEnd);
             }
             wsInstance.un('ready', handleReady);
             wsInstance.un('error', handleError);
             wsInstance.un('audioprocess', handleAudioprocess);
             wsInstance.un('seeking', handleSeeking);
             wsInstance.un('play', handlePlay);
             wsInstance.un('pause', handlePause);
             wsInstance.un('finish', handleFinish);

             // Destroy with try-catch to prevent errors
             try {
                 wsInstance.destroy();
             } catch (e) {
                 console.warn('[WaveformEditor] Error during destroy:', e);
             }
        }

        // Clean up Web Audio gain nodes
        if (mediaSourceRef.current) {
            try {
                mediaSourceRef.current.disconnect();
            } catch (e) {
                // Ignore disconnect errors
            }
            mediaSourceRef.current = null;
        }
        if (gainNodeRef.current) {
            try {
                gainNodeRef.current.disconnect();
            } catch (e) {
                // Ignore disconnect errors
            }
            gainNodeRef.current = null;
        }
        if (audioContextRef.current) {
            try {
                audioContextRef.current.close();
            } catch (e) {
                // Ignore close errors
            }
            audioContextRef.current = null;
        }
    };
  }, [open, audioUrl, waveformContainer]); // Remove function dependencies to prevent constant re-rendering



  const handlePlayPause = useCallback(() => {
    console.log('🎵🎵🎵 WAVEFORM EDITOR DEBUG - handlePlayPause called. isPlaying:', isPlaying);
    console.log('🎵🎵🎵 WAVEFORM EDITOR DEBUG - Current state - currentInPoint:', currentInPoint, 'currentOutPoint:', currentOutPoint);
    
    // If already playing, just pause
    if (isPlaying) {
      console.log('[WaveformEditor DEBUG] handlePlayPause: Already playing, pausing now.');
      if (wavesurfer.current) {
        wavesurfer.current.pause();
      }
      return;
    }
    
    // Otherwise, play from the current playhead position (do NOT seek to IN)
    if (wavesurfer.current) {
      console.log('[WaveformEditor DEBUG] handlePlayPause: Playing from current playhead position.');
      const ws = wavesurfer.current;
      const duration = ws.getDuration();

      if (duration > 0) {
        try {
          // Regular play should not mark region playback
          setIsPlayingRegion(false);
          ws.play();
          setIsPlaying(true);
        } catch (e) {
          console.error('[WaveformEditor DEBUG] handlePlayPause: Error starting playback:', e);
          setIsPlaying(false);
        }
      } else {
        console.warn('[WaveformEditor DEBUG] handlePlayPause: Attempted to play, but duration is not available yet.');
      }
    } else {
      // Fallback to normal play/pause if no wavesurfer
      console.log('[WaveformEditor DEBUG] handlePlayPause: No wavesurfer available.');
    }
  }, [isPlaying]);

  // Log when handlePlayPause function is created
  console.log('🎵 WAVEFORM EDITOR DEBUG - handlePlayPause function created');

  // Add handler to play only the selected region (IN to OUT)
  const handlePlayRegion = useCallback(() => {
    console.log(`🔥🔥🔥 PLAY REGION BUTTON CLICKED! 🔥🔥🔥`);
    if (wavesurfer.current) {
        const ws = wavesurfer.current;
        const duration = ws.getDuration();
        
        if (duration > 0) {
            if (ws.isPlaying()) {
                ws.pause();
            }
            
            const inPoint = inPointRef.current;
            const outPoint = outPointRef.current;
            const seekTarget = inPoint / duration;
            ws.seekTo(seekTarget);

            setTimeout(() => {
                if (wavesurfer.current) {
                    wavesurfer.current.play();

                    // Set timer to stop at OUT point (read live refs for the latest edited points)
                    const regionDuration = (outPoint - inPoint) * 1000; // Convert to milliseconds
                    console.log(`Setting timer to stop in ${regionDuration}ms`);
                    
                    setTimeout(() => {
                        if (wavesurfer.current && wavesurfer.current.isPlaying()) {
                            console.log(`TIMER STOPPING AT OUT POINT!`);
                            wavesurfer.current.pause();
                            setIsPlaying(false);
                            setIsPlayingRegion(false);
                        }
                    }, regionDuration);
                }
            }, 50);
        }
    }
  }, [currentInPoint, currentOutPoint]);

  const handleSave = () => {
    // Only save in/out points - don't modify gain settings
    onSave(lineKey, currentInPoint, currentOutPoint);
    onClose();
  };
  
  const formatTime = (seconds: number): string => {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toFixed(2).padStart(5, '0')}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {
          (lineKey === 'Intro' || lineKey === 'Outro') 
            ? lineKey 
            : (songTitle && songArtist) 
              ? `${lineKey} - ${songTitle} by ${songArtist}` 
              : lineKey // Fallback to just lineKey if title/artist missing for numbered lines
        }
      </DialogTitle>
      <DialogContent>
        {/* Wrapper with relative positioning for absolute slider placement */}
        <Box sx={{ position: 'relative', mb: 2 }}>
          {/* Waveform container - 90% width to leave room for slider */}
          <Box
            ref={setWaveformContainer}
            sx={{
              width: '90%',
              minHeight: '100px',
              position: 'relative',
              border: '1px solid #ccc',
              background: '#f0f0f0',
              // Widen the invisible grab area on the region's resize handles
              // without changing how they look. WaveSurfer renders each handle
              // as a 6px-wide div whose visible line is a 2px inner border; the
              // border stays anchored to the edge (left:0 / right:0) while the
              // wider transparent box just extends inward, making them far
              // easier to grab. WaveSurfer sets width inline, so !important.
              '& [part*="region-handle"]': {
                width: '16px !important',
              },
              // Keep the visible 2px lines exactly where they were.
              '& [part*="region-handle-left"]': {
                borderLeftWidth: '2px',
              },
              '& [part*="region-handle-right"]': {
                borderRightWidth: '2px',
              },
            }}
          >

              {isLoading && (
                  <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.8)' }}>
                      <CircularProgress />
                      <Typography sx={{ ml: 1 }}>Loading audio...</Typography>
                  </Box>
              )}
              {error && <Typography color="error" sx={{ p: 2 }}>{error}</Typography>}
          </Box>

          {/* Vertical Zoom Slider - positioned in the remaining 10% space */}
          <Box sx={{ 
            position: 'absolute', 
            top: 0, 
            right: 0, // Position in the right 10% of the container
            width: '10%',
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            height: '100%',
            justifyContent: 'flex-start'
          }}>
              <Typography variant="caption" sx={{ mb: 1, fontSize: '16px', fontWeight: 'bold' }}>+</Typography>
              {/* Log the state value right before rendering the slider (valid JSX way) */}
              {(() => { 
                  console.log(`[WaveformEditor DEBUG] Rendering Zoom Slider with minPxPerSec: ${minPxPerSec}`); 
                  return null; 
              })()}
              <Slider
                  orientation="vertical"
                  size="small"
                  value={minPxPerSec}
                  min={10}  // Minimum zoom level (most zoomed out)
                  max={500} // Maximum zoom level (most zoomed in)
                  step={10}
                  onChange={(event: Event, value: number | number[]) => {
                      // Handle potential array value if slider has multiple thumbs (though unlikely here)
                      const newZoom = typeof value === 'number' ? value : value[0]; 
                      console.log(`[WaveformEditor DEBUG] Zoom slider onChange fired! New value: ${newZoom}`);
                      setMinPxPerSec(newZoom); // Use state setter from component scope
                      if (wavesurfer.current) {
                          console.log(`[WaveformEditor DEBUG] Calling wavesurfer.zoom(${newZoom})`);
                          wavesurfer.current.zoom(newZoom);
                      } else {
                          console.warn('[WaveformEditor DEBUG] Cannot call zoom, wavesurfer.current is null.');
                      }
                  }}
                  // Add onMouseDown for basic click detection
                  onMouseDown={() => {
                      console.log('[WaveformEditor DEBUG] Zoom slider MouseDown event fired!');
                  }}
                  disabled={isLoading || !!error}
                  sx={{ height: '80px', flex: 1 }}
                  valueLabelDisplay="auto"
              />
              <Typography variant="caption" sx={{ mt: 1, fontSize: '16px', fontWeight: 'bold' }}>-</Typography>
          </Box>
        </Box>
        
        
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-evenly', gap: 2, mb: 2, borderTop: '1px solid #eee', pt: 2 }}>
            <IconButton onClick={(e) => {
                console.log('🎵🎵🎵 MAIN PLAY BUTTON CLICKED! 🎵🎵🎵');
                handlePlayPause();
            }} disabled={isLoading || !!error} color="primary" size="large">
                {isPlaying ? <Pause /> : <PlayArrow />}
            </IconButton>
            <IconButton
                ref={playRegionButtonRef}
                disabled={isLoading || !!error}
                title="Play In to Out"
                data-testid="play-region-button"
                color="primary"
                size="large"
                onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handlePlayRegion();
                }}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handlePlayRegion();
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                    <Box sx={{ width: 2, height: 24, bgcolor: 'currentColor', borderRadius: 1 }} />
                    <PlayArrow sx={{ color: 'currentColor' }} />
                    <Box sx={{ width: 2, height: 24, bgcolor: 'currentColor', borderRadius: 1 }} />
                </Box>
            </IconButton>
            <Typography variant="body2">In: <strong>{currentInPoint.toFixed(2)}s</strong></Typography>
            <Box sx={{ 
                border: '2px solid', 
                borderColor: 'primary.main', 
                borderRadius: 1, 
                px: 1, 
                py: 0.5 
            }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main', fontFamily: 'monospace', fontSize: '1.2rem' }}>{currentTime.toFixed(2)}s</Typography>
            </Box>
            <Typography variant="body2">Out: <strong>{currentOutPoint.toFixed(2)}s</strong></Typography>
        </Box>


      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} startIcon={<Cancel />}>Cancel</Button>
        <Button 
            onClick={handleSave} 
            variant="contained" 
            startIcon={<Save />} 
            disabled={isLoading || !!error} 
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}); 